import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RangeBar from "./RangeBar";
import CompsGrid from "./CompsGrid";
import PropertyDetails from "./PropertyDetails";
import ValuationHistory from "./ValuationHistory";
import PublicRecords from "./PublicRecords";
import { money } from "./format";

// The revealed AVM result (SUR-2302 / SSD-23). The headline estimate + range
// stays in the hero capture card; the detailed breakdown (property details,
// comps, value history, public records) is PORTALED into a full-width section
// below the hero (#avm-report-slot in home_value_report.liquid) so it flows on
// the page in the detail styling instead of being crammed into the narrow card.
//
// When the portal target isn't in the DOM (custom/agent-authored templates
// mount the island without one) an equivalent full-width section is created
// after the island's own section and used as the portal target — see the slot
// effect below. Only when that's impossible does the breakdown render inline
// beneath the card, stacked single-column.
// SSD-23 hero stat blocks (value over small-caps label) from the AVM property.
const STAT_FIELDS = [
  { key: "beds", label: "Beds", fmt: (v) => v },
  { key: "baths", label: "Baths", fmt: (v) => v },
  { key: "sqft", label: "Sq Ft", fmt: (v) => v.toLocaleString() },
  { key: "year_built", label: "Year Built", fmt: (v) => v },
  { key: "lot_size_acres", label: "Acre Lot", fmt: (v) => v },
  { key: "garage_cars", label: "Car Garage", fmt: (v) => v },
];

export default function RevealedReport({
  flow,
  agentName,
  agentPhoto,
  agentEmail,
  agentPhone,
  agentBrokerage,
  showPropertyDetails,
  showValuationHistory,
  showComparables,
  showPublicRecords,
  sectionOrder,
  detailsHeading,
  historyHeading,
  comparablesHeading,
  deedsHeading,
  mortgagesHeading,
  reportSlotId,
  reportSectionId,
}) {
  const v = flow.valuation || {};
  const hasAgentEstimate = flow.agentEstimate != null && flow.agentEstimate !== "";

  // Section toggles: a section shows unless the template EXPLICITLY disabled it.
  // The props arrive from Liquid as JSON, so an un-customized page (or a page
  // config saved before these fields existed) passes `null`, which must NOT hide
  // the section — only a deliberate `false` does.
  const on = (value) => value !== false;
  const visibleByKey = {
    property_details: on(showPropertyDetails),
    valuation_history: on(showValuationHistory),
    comparables: on(showComparables),
    public_records: on(showPublicRecords),
  };
  // Render sub-sections in the agent's resolved order (SUR-2270), falling back to
  // the natural order. Unknown/duplicate keys are ignored; any known key missing
  // from the supplied order is appended so nothing silently drops.
  const NATURAL_ORDER = ["property_details", "valuation_history", "comparables", "public_records"];
  const requested = Array.isArray(sectionOrder) ? sectionOrder.filter((k) => k in visibleByKey) : [];
  const orderedKeys = [...new Set([...requested, ...NATURAL_ORDER])];
  const sectionFor = (key) => {
    switch (key) {
      case "property_details":
        return visibleByKey.property_details
          ? <PropertyDetails key={key} property={flow.property} heading={detailsHeading} /> : null;
      case "valuation_history":
        return visibleByKey.valuation_history
          ? <ValuationHistory key={key} history={v.history} heading={historyHeading} /> : null;
      case "comparables":
        return visibleByKey.comparables
          ? <CompsGrid key={key} comps={flow.comps} heading={comparablesHeading} /> : null;
      case "public_records":
        return visibleByKey.public_records
          ? (
            <PublicRecords
              key={key}
              deeds={flow.deeds}
              mortgages={flow.mortgages}
              deedsHeading={deedsHeading}
              mortgagesHeading={mortgagesHeading}
            />
          ) : null;
      default:
        return null;
    }
  };
  const sections = orderedKeys.map(sectionFor).filter(Boolean);
  const hasDetail = sections.length > 0;

  const section = typeof document !== "undefined" ? document.getElementById(reportSectionId) : null;
  // The report hero (photo/price/address/stats) renders even when every managed
  // section is toggled off, so "has anything to portal" includes it.
  const property = flow.property || {};
  const leadEstimate = hasAgentEstimate ? flow.agentEstimate : v.estimate;
  const hasHero = Boolean(property.address || property.image || leadEstimate);
  const needsSlot = hasDetail || hasHero;

  // Where the breakdown portals. The system home_value_report template ships a
  // full-width #avm-report-slot section below the hero, but custom
  // (agent-authored) templates that mount this island usually don't include
  // one — and the full report (photo, display typography, the 320px contact
  // rail) cannot fit inside the narrow capture card: the photo and rail
  // overflow the card and cover the surrounding page (found in live QA). When
  // the slot is missing, create an equivalent full-width section right after
  // the island's own <section> and portal there instead. Rendering the report
  // inline stays only as a last resort for a mount with no insertable parent,
  // stacked single-column via the --stack modifier so nothing overflows.
  const rootRef = useRef(null);
  const createdSectionRef = useRef(null);
  const [slot, setSlot] = useState(() =>
    (typeof document !== "undefined" ? document.getElementById(reportSlotId) : null));
  const [slotUnavailable, setSlotUnavailable] = useState(false);

  // Layout effect so the slot exists — and the portal re-render flushes —
  // before the revealed report first paints.
  useLayoutEffect(() => {
    if (!needsSlot || slot) return;
    const existing = document.getElementById(reportSlotId);
    if (existing) {
      setSlot(existing);
      return;
    }
    const mount = rootRef.current;
    const island = mount && (mount.closest("[data-connect-island]") || mount);
    // Insert after the island's enclosing <section> (how the system template
    // places the slot). Custom markup without sections: climb to the island's
    // top-level block inside <main> (or <body>) so the report is never created
    // inside a narrow wrapper like the capture card.
    let host = island && island.closest("section");
    if (!host && island) {
      const boundary = document.querySelector("main") || document.body;
      host = island;
      while (host.parentElement && host.parentElement !== boundary) host = host.parentElement;
      if (!host.parentElement) host = null;
    }
    if (!host || !host.parentNode) {
      setSlotUnavailable(true);
      return;
    }
    const sectionEl = document.createElement("section");
    sectionEl.className = "ss-c-section";
    const container = document.createElement("div");
    container.className = "ss-c-container";
    sectionEl.appendChild(container);
    host.parentNode.insertBefore(sectionEl, host.nextSibling);
    createdSectionRef.current = sectionEl;
    setSlot(container);
  }, [needsSlot, slot, reportSlotId]);

  // Remove the section we created (if any) when the island unmounts — e.g. the
  // flow returning to the address search.
  useEffect(() => () => {
    createdSectionRef.current?.remove();
    createdSectionRef.current = null;
  }, []);

  const portaled = needsSlot && slot;

  // Reveal the (server-rendered, hidden) report section only when there's a
  // portaled breakdown to show; hide it again if this island unmounts.
  useEffect(() => {
    if (!section) return undefined;
    section.hidden = !portaled;
    return () => { section.hidden = true; };
  }, [section, portaled]);

  const estimate = (
    <div className="ssc-report-estimate" ref={rootRef}>
      {hasAgentEstimate ? (
        <>
          <div className="ssc-report-estimate-label">Agent estimate</div>
          <div className="ssc-report-estimate-num">{money(flow.agentEstimate)}</div>
          <div className="ssc-report-estimate-sub">
            <div className="ssc-report-estimate-label">Automated estimate</div>
            <div className="ssc-report-estimate-num">{money(v.estimate)}</div>
          </div>
        </>
      ) : (
        <>
          <div className="ssc-report-estimate-label">Estimated value</div>
          <div className="ssc-report-estimate-num">{money(v.estimate)}</div>
        </>
      )}
      {v.confidence != null && v.confidence !== "" && (
        <div className="ssc-report-confidence">
          Confidence: {typeof v.confidence === "number" ? `${v.confidence}%` : v.confidence}
        </div>
      )}
      <RangeBar low={v.low} high={v.high} estimate={v.estimate} />
      {portaled && <p className="ssc-report-hint">Your full report is below ↓</p>}
    </div>
  );

  // --- SSD-23 report hero: photo → gold price → big address → stat blocks ---
  // "1428 Maple Crest Dr, Austin, TX 78704" → street line + city/state line.
  const [street, ...rest] = (property.address || "").split(",");
  const cityState = rest.join(",").trim();
  const stats = STAT_FIELDS.filter(({ key }) => {
    const value = property[key];
    return value !== undefined && value !== null && value !== "";
  });
  const hero = hasHero && (
    <section className="ssc-report-section">
      {property.image && (
        // The property photo is often a Google Street View URL, which can fail
        // (no imagery for the address, key restrictions). Hide on error so a
        // broken-image icon never leads the report (found in live QA).
        <img
          className="ssc-report-img"
          src={property.image}
          alt={property.address || "Your home"}
          loading="lazy"
          onError={(e) => { e.currentTarget.hidden = true; }}
        />
      )}
      {leadEstimate && <div className="ssc-report-price">{money(leadEstimate)}</div>}
      {street && <h2 className="ssc-report-addr1">{street.trim()}</h2>}
      {cityState && <div className="ssc-report-addr2">{cityState}</div>}
      {stats.length > 0 && (
        <div className="ssc-report-stats">
          {stats.map(({ key, label, fmt }) => (
            <div key={key}>
              <div className="ssc-report-stat-v">{fmt(property[key])}</div>
              <div className="ssc-report-stat-k">{label}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  // --- SSD-23 sticky agent rail: ask-about-this-report card + agent chip -----
  const rail = (agentEmail || agentPhone) && (
    <aside className="ssc-report-rail">
      <div className="ssc-report-rail-card">
        <h3>Questions about this report?</h3>
        <p>
          {agentName} can walk you through your estimate, the comparable sales behind it,
          and what it means for your plans.
        </p>
        {agentEmail && (
          <button
            type="button"
            className="ss-c-btn"
            style={{ display: "block", width: "100%", textAlign: "center" }}
            onClick={() => window.dispatchEvent(new CustomEvent("connect:contact-open"))}
          >
            Ask {agentName}
          </button>
        )}
        {agentPhone && (
          <a
            className="ss-c-btn ss-c-btn--ghost"
            style={{ display: "block", textAlign: "center", marginTop: "0.6rem" }}
            href={`tel:${agentPhone}`}
          >
            Call now
          </a>
        )}
        {(agentPhoto || agentBrokerage) && (
          <div className="ssc-agent-chip">
            {agentPhoto && <img src={agentPhoto} alt={agentName} />}
            <div>
              <div className="ssc-agent-chip-name">{agentName}</div>
              {agentBrokerage && <div className="ssc-agent-chip-sub">{agentBrokerage}</div>}
            </div>
          </div>
        )}
      </div>
    </aside>
  );

  const detail = (hero || hasDetail) ? (
    <div className={`ssc-report-layout${slot ? "" : " ssc-report-layout--stack"}`}>
      <div className="ssc-report-main">
        {hero}
        {hasDetail && <div className="ssc-report">{sections}</div>}
      </div>
      {rail}
    </div>
  ) : null;

  return (
    <>
      {estimate}
      {/* Portal the breakdown to the full-width slot (found or created above).
          Until the slot resolves render nothing — an inline flash of the full
          report inside the capture card overflows it. Inline (stacked) render
          only when no slot could be created at all. */}
      {detail && (slot ? createPortal(detail, slot) : (slotUnavailable ? detail : null))}
    </>
  );
}
